import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';

import { BannerActionType, useDispatchExternalOfferActionMutation, type ExternalOfferFragment } from '@ocx/graphql';

import { offerMessages } from '../offer.messages';

export type UseActivateExternalBanner = () => {
  activate: (banner: ExternalOfferFragment) => Promise<boolean>;
  isActivating: boolean;
};

export const useActivateExternalBanner: UseActivateExternalBanner = () => {
  const intl = useIntl();
  const snackbar = useSnackbar();

  const onError = useCallback(() => {
    snackbar.open(intl.formatMessage(offerMessages.activationFailed));
  }, [intl, snackbar]);

  const [dispatchExternalOfferAction, { loading: isActivating }] = useDispatchExternalOfferActionMutation({
    refetchQueries: ['GetExternalOffers'],
  });

  const activate = useCallback(
    async (banner: ExternalOfferFragment) => {
      try {
        const result = await dispatchExternalOfferAction({
          variables: {
            input: {
              bannerId: banner.id,
              actionType: BannerActionType.Activate,
            },
          },
        });

        if (!result.data?.dispatchBannerAction?.success) {
          throw new Error('dispatchBannerAction.success is not true');
        }
        return true;
      } catch {
        onError();
        return false;
      }
    },
    [dispatchExternalOfferAction, onError],
  );

  return useMemo(
    () => ({
      activate,
      isActivating,
    }),
    [activate, isActivating],
  );
};
